import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Zap, Info, RotateCcw, Eye, EyeOff } from "lucide-react";
import ItemHints from "../components/ItemHints";
import FavoriteItems from "../components/FavoriteItems";
import api, { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

const UNITS = ["kg", "g", "l", "ml", "pz", "cassa", "cartone", "sacco"];

export default function CreateOrder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [address, setAddress] = useState(user?.address || "");
  const [notes, setNotes] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [hours, setHours] = useState(12);
  const [items, setItems] = useState([{ _key: 1, name: "", quantity: 1, unit: "kg", notes: "" }]);
  const [loading, setLoading] = useState(false);
  const [directMode, setDirectMode] = useState(false);
  const [directSupplierId, setDirectSupplierId] = useState("");
  const [expressMode, setExpressMode] = useState(false);
  const [sealed, setSealed] = useState(false);
  const [expressMaxPrice, setExpressMaxPrice] = useState("");

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/restaurant/favorites").then(({ data }) => setFavorites(data)).catch(() => {});
    const from = new URLSearchParams(window.location.search).get("from");
    if (from) {
      api.get(`/orders/${from}`).then(({ data: o }) => {
        setTitle(o.title || "");
        setCategory(o.category || "");
        setAddress(o.delivery_address || user?.address || "");
        setNotes(o.notes || "");
        setTimeFrom(o.delivery_time_from || "");
        setTimeTo(o.delivery_time_to || "");
        if (o.items?.length) setItems(o.items.map((it, i) => ({ _key: Date.now() + i, name: it.name || "", quantity: it.quantity || 1, unit: it.unit || "kg", notes: it.notes || "" })));
        setReorderFrom(o.title || from);
        toast.success("Ordine precompilato dal tuo ordine precedente: controlla data e quantità, poi pubblica");
      }).catch(() => toast.error("Ordine di origine non trovato"));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [reorderFrom, setReorderFrom] = useState("");

  const eligibleFavorites = category ? favorites.filter(f => f.categories.some(c => c.id === category)) : favorites;

  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, { _key: Date.now() + Math.random(), name: "", quantity: 1, unit: "kg", notes: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const pickFavorite = (fav) => {
    const blank = items.findIndex(it => !it.name.trim());
    const row = { _key: Date.now() + Math.random(), name: fav.name, quantity: fav.quantity || 1, unit: fav.unit || "kg", notes: fav.notes || "" };
    setItems(blank >= 0 ? items.map((it, idx) => idx === blank ? { ...row, _key: it._key } : it) : [...items, row]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error("Seleziona una categoria"); return; }
    if ((timeFrom || timeTo) && (!timeFrom || !timeTo)) { toast.error("Indica inizio e fine della fascia oraria"); return; }
    if (timeFrom && timeTo) {
      const [h1, m1] = timeFrom.split(":").map(Number); const [h2, m2] = timeTo.split(":").map(Number);
      if ((h2 * 60 + m2) - (h1 * 60 + m1) < 60) { toast.error("La fascia oraria deve durare almeno 1 ora"); return; }
    }
    if (items.some(i => !i.name || !i.quantity)) { toast.error("Compila tutti gli articoli"); return; }
    if (directMode && !directSupplierId) { toast.error("Seleziona un fornitore preferito"); return; }
    if (expressMode && (!expressMaxPrice || Number(expressMaxPrice) <= 0)) { toast.error("Imposta un prezzo massimo per la modalità express"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        title, category, items,
        delivery_address: address,
        delivery_date: deliveryDate || null,
        delivery_time_from: timeFrom || null,
        delivery_time_to: timeTo || null,
        notes, auction_hours: Number(hours),
        direct_supplier_id: directMode ? directSupplierId : null,
        express_max_price: expressMode ? Number(expressMaxPrice) : null,
        auction_visibility: sealed ? "sealed" : "transparent",
      });
      toast.success(directMode ? "Ordine diretto inviato al fornitore" : (expressMode ? "Ordine express pubblicato · il primo fornitore che accetta vince" : "Ordine pubblicato!"));
      navigate(`/ordine/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Strict block: unrated deliveries → redirect user to the first pending order
      if (detail && typeof detail === "object" && detail.code === "pending_ratings" && detail.first_order_id) {
        toast.error(detail.message || "Valuta le ultime consegne prima di creare un nuovo ordine");
        navigate(`/ordine/${detail.first_order_id}`);
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">{reorderFrom ? "Riordina" : "Nuovo ordine"}</h1>
      {reorderFrom && <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1 text-xs font-semibold" data-testid="reorder-badge"><RotateCcw className="w-3.5 h-3.5" /> Copia di "{reorderFrom}" · articoli e indirizzo già compilati</div>}
      <p className="mt-1 text-slate-600 text-sm">I fornitori della categoria selezionata riceveranno la richiesta e potranno fare offerte al ribasso.</p>

      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="order-quality-notice">
        <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <span className="font-semibold">Descrivi bene cosa vuoi ricevere.</span> Indica con precisione la <b>categoria</b> e la <b>qualità</b> della merce (es. origine, calibro, pezzatura, fresco/congelato, IGP/DOP, marca o equivalente) e le quantità nelle note di ogni articolo: il fornitore è vincolato a consegnare esattamente quanto descritto e una descrizione chiara evita contestazioni alla consegna.
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="create-order-form">
        <div className="ga-card p-6 space-y-4">
          <div>
            <Label htmlFor="title">Titolo ordine</Label>
            <Input id="title" required value={title} onChange={e => setTitle(e.target.value)} placeholder="Consegna settimanale carne" data-testid="order-title-input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Categoria</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="order-category-select"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                <SelectContent>
                  {cats.map(c => <SelectItem key={c.id} value={c.id} data-testid={`order-category-option-${c.id}`}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="hours">Durata asta (ore)</Label>
              <Input id="hours" type="number" min={1} max={12} value={hours} onChange={e => setHours(e.target.value)} data-testid="order-hours-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Data consegna richiesta</Label>
              <Input id="date" type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} data-testid="order-date-input" />
            </div>
            <div>
              <Label>Fascia oraria consegna (min. 1 ora)</Label>
              <div className="flex items-center gap-2">
                <Input type="time" step={900} value={timeFrom} onChange={e => setTimeFrom(e.target.value)} data-testid="order-time-from-input" />
                <span className="text-slate-500 text-sm">–</span>
                <Input type="time" step={900} value={timeTo} onChange={e => setTimeTo(e.target.value)} data-testid="order-time-to-input" />
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {[["07:00","09:00"],["09:00","11:00"],["11:00","13:00"],["14:00","16:00"],["16:00","18:00"]].map(([a,b]) => (
                  <button key={a} type="button" onClick={() => { setTimeFrom(a); setTimeTo(b); }}
                    className={`px-2 py-0.5 rounded-full border text-[11px] font-mono ${timeFrom === a && timeTo === b ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`slot-${a.replace(":","")}`}>{a}–{b}</button>
                ))}
                {(timeFrom || timeTo) && <button type="button" onClick={() => { setTimeFrom(""); setTimeTo(""); }} className="px-2 py-0.5 text-[11px] text-slate-500 underline" data-testid="slot-clear">nessuna</button>}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="address">Indirizzo consegna</Label>
              <Input id="address" required value={address} onChange={e => setAddress(e.target.value)} placeholder="Via Roma 10, 10121 Torino" data-testid="order-address-input" />
              <div className="text-[11px] text-slate-500 mt-1" data-testid="address-hint">Obbligatori via e civico, <b>CAP</b> e <b>città</b>: i fornitori vedono solo CAP e città (zona) finché non si aggiudicano l'ordine, così possono stimare la consegna senza conoscere l'indirizzo esatto.</div>
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Note (opzionale)</Label>
            <Textarea id="notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Preferenze, orari consegna..." data-testid="order-notes-input" />
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Ordine diretto (salta l'asta)
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Assegna subito a un fornitore preferito, ideale per urgenze.</div>
              </div>
              <Switch checked={directMode} onCheckedChange={(v) => { setDirectMode(v); if (v) setExpressMode(false); }} data-testid="direct-mode-switch" />
            </div>
            {directMode && (
              <div className="mt-3">
                {eligibleFavorites.length === 0 ? (
                  <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    Nessun fornitore preferito {category ? "per questa categoria" : ""}. Aggiungi preferiti dalla scheda di un fornitore.
                  </div>
                ) : (
                  <div>
                    <Label>Fornitore preferito</Label>
                    <Select value={directSupplierId} onValueChange={setDirectSupplierId}>
                      <SelectTrigger data-testid="direct-supplier-select"><SelectValue placeholder="Scegli fornitore" /></SelectTrigger>
                      <SelectContent>
                        {eligibleFavorites.map(f => (
                          <SelectItem key={f.id} value={f.id} data-testid={`direct-supplier-${f.id}`}>
                            {f.name} · ★ {f.rating_avg}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  <Zap className="w-4 h-4 text-emerald-600" />
                  Modalità express (una-sola-offerta)
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Imposta un prezzo massimo. Il primo fornitore che accetta vince, senza asta.</div>
              </div>
              <Switch checked={expressMode} onCheckedChange={(v) => { setExpressMode(v); if (v) setDirectMode(false); }} data-testid="express-mode-switch" />
            </div>
            {expressMode && (
              <div className="mt-3">
                <Label htmlFor="express-price">Prezzo massimo (€)</Label>
                <Input id="express-price" type="number" min={0.01} step="0.01" value={expressMaxPrice} onChange={e => setExpressMaxPrice(e.target.value)} placeholder="es. 250.00" data-testid="express-max-price-input" />
                <div className="text-xs text-slate-500 mt-1">Chiunque accetta questo prezzo si aggiudica subito l'ordine.</div>
              </div>
            )}
            {!expressMode && !directMode && (
              <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between gap-3" data-testid="auction-visibility-row">
                <div>
                  <div className="flex items-center gap-2 font-semibold text-slate-900">{sealed ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-emerald-600" />} {sealed ? "Asta a busta chiusa" : "Asta trasparente"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{sealed ? "I fornitori non vedono il miglior prezzo: ognuno offre il suo prezzo migliore alla cieca." : "I fornitori vedono il miglior prezzo in tempo reale e ricevono un avviso quando vengono superati: più rilanci al ribasso."}</div>
                </div>
                <Switch checked={!sealed} onCheckedChange={(v) => setSealed(!v)} data-testid="auction-visibility-switch" />
              </div>
            )}
          </div>
        </div>

        <div className="ga-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900">Articoli</h2>
            <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-full" data-testid="add-item-button"><Plus className="w-4 h-4 mr-1" />Aggiungi</Button>
          </div>
          <FavoriteItems category={category} existing={items} onPick={pickFavorite} />
          <div className="mt-4 space-y-3">
            {items.map((it, i) => (
              <div key={it._key ?? i} className="grid grid-cols-12 gap-2 items-end" data-testid={`item-row-${i}`}>
                <div className="col-span-12 sm:col-span-5">
                  <Label className="text-xs">Prodotto</Label>
                  <Input value={it.name} onChange={e => updateItem(i, { name: e.target.value })} placeholder="Bistecca di manzo" data-testid={`item-name-${i}`} />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-xs">Q.tà</Label>
                  <Input type="number" min={0} step="0.1" value={it.quantity} onChange={e => updateItem(i, { quantity: Number(e.target.value) })} data-testid={`item-qty-${i}`} />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-xs">Unità</Label>
                  <Select value={it.unit} onValueChange={v => updateItem(i, { unit: v })}>
                    <SelectTrigger data-testid={`item-unit-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-xs">Note</Label>
                  <Input value={it.notes} onChange={e => updateItem(i, { notes: e.target.value })} placeholder="Origine, calibro, pezzatura..." data-testid={`item-notes-${i}`} />
                </div>
                <div className="col-span-12 sm:col-span-1 flex justify-end">
                  {items.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} data-testid={`item-remove-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <ItemHints category={category} notes={it.notes} index={i} onAddChip={(v) => updateItem(i, { notes: v })} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-full" data-testid="cancel-order-button">Annulla</Button>
          <Button type="submit" disabled={loading} className="ga-btn-primary rounded-full" data-testid="submit-order-button">
            {loading ? "Pubblicazione..." : "Pubblica ordine"}
          </Button>
        </div>
      </form>
    </div>
  );
}
